import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapIcon, ListIcon, PencilIcon, SearchXIcon, SlidersHorizontalIcon, UsersIcon } from 'lucide-react';
import { SearchIntentPanel } from '../../components/housing/SearchIntentPanel';
import { FilterRail } from '../../components/housing/FilterRail';
import { ListingCard } from '../../components/housing/ListingCard';
import { ListingMap } from '../../components/housing/ListingMap';
import { Panel } from '../../components/ui/Panel';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { EmptyState } from '../../components/ui/EmptyState';
import { Modal } from '../../components/ui/Modal';
import { listings, myApplications } from '../../data/housing';
import { campusById } from '../../data/institutions';
import { useAccount } from '../../contexts/AccountContext';
import { useDataMode } from '../../contexts/DataModeContext';
import { DEFAULT_FILTERS, applyHousingSearch } from '../../utils/housing';
import { naira } from '../../utils/format';
import type { HousingFilters, SearchIntent } from '../../types/housing';
type Stage = 'intent' | 'searching' | 'results';
export function Housing() {
  const {
    account
  } = useAccount();
  const {
    isNew
  } = useDataMode();
  const [stage, setStage] = useState<Stage>('intent');
  const [intent, setIntent] = useState<SearchIntent>({
    campusId: account.campusId,
    budgetMax: 750000,
    moveIn: '2026-09-01',
    kinds: []
  });
  const [filters, setFilters] = useState<HousingFilters>(DEFAULT_FILTERS);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [mobileView, setMobileView] = useState<'list' | 'map'>('list');
  const [filtersOpen, setFiltersOpen] = useState(false);
  useEffect(() => {
    if (stage !== 'searching') return;
    const timer = window.setTimeout(() => setStage('results'), 550);
    return () => window.clearTimeout(timer);
  }, [stage]);
  const results = useMemo(() => applyHousingSearch(listings, intent, filters), [intent, filters]);
  const place = campusById(intent.campusId);
  if (stage === 'intent') {
    return <div className="space-y-10 py-6" data-id="element-9504" data-mp="2sn64dw1gu2xe">
        <SearchIntentPanel initial={intent} onSearch={next => {
        setIntent(next);
        setFilters(DEFAULT_FILTERS);
        setStage('searching');
      }} data-id="element-9505" data-mp="2sm4tbrkg9gfo" />

        <div className="mx-auto grid max-w-3xl gap-6 sm:grid-cols-2" data-id="element-9506" data-mp="2iugxlegv1d8e">
          <Panel title="Your applications" bodyClassName={isNew ? undefined : 'p-0'} data-id="element-9507" data-mp="2nouu5k79dqen">
            {isNew ? <p className="max-w-[60ch] text-body-sm text-ink-muted" data-id="element-9508" data-mp="29xq2p5e1o4fe">
                You have not applied anywhere yet. Search above, and once you apply the property's
                decision, what they still need from you, and your move-in date all track here.
                Campuzon never applies on your behalf.
              </p> : <ul className="divide-y divide-line" data-id="element-9509" data-mp="1lah1jzsd2kq9">
              {myApplications.map(application => <li key={application.id} className="px-5 py-3.5" data-id="element-9510" data-mp="1ha6uskwqvqac">
                  <p className="text-[13px] font-medium text-ink" data-id="element-9511" data-mp="1v96h3lwsbt3c">{application.listingName}</p>
                  <p className="mt-0.5 text-[12px] text-ink-muted" data-id="element-9512" data-mp="1v6tu2uc66sjf">
                    Submitted {application.submitted} · move-in {application.moveIn}
                  </p>
                  <Badge tone={application.status === 'in_review' ? 'brand' : 'neutral'} className="mt-2" data-id="element-9513" data-mp="2481qf6v4ljav">
                    {application.status === 'in_review' ? 'In review' : 'Submitted'}
                  </Badge>
                </li>)}
            </ul>}
          </Panel>
          <Panel title="Looking for a roommate?" data-id="element-9514" data-mp="2nx2eyty3br1p">
            <p className="text-[13px] leading-6 text-ink-muted" data-id="element-9515" data-mp="2nvr5cdhyrp2o">
              Set your living preferences once and see students whose declared preferences overlap
              with yours. You decide what they can see before anyone makes contact.
            </p>
            <Link to="/housing/roommates" className="mt-4 inline-block" data-id="element-9516" data-mp="l9vmjo7o2372">
              <Button variant="secondary" icon={<UsersIcon className="h-4 w-4" data-id="element-9518" data-mp="q2f2grtttaf1" />} data-id="element-9517" data-mp="q1dghsb9v3ij">
                Roommate matching
              </Button>
            </Link>
          </Panel>
        </div>
      </div>;
  }
  return <div className="space-y-4" data-id="element-9519" data-mp="3tjx8ctq1g7ct">
      <div className="flex flex-col gap-3 border-b border-line pb-4 lg:flex-row lg:items-center lg:justify-between" data-id="element-9520" data-mp="3tkq83aqkxwlg">
        <div className="min-w-0" data-id="element-9521" data-mp="7bf5p4g3awb7">
          <h1 className="text-h3 font-semibold tracking-tight text-ink" data-id="element-9522" data-mp="7b5ni31adtg2">
            Housing near {place?.campus.name}
          </h1>
          <p className="cz-num mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-body-sm text-ink-muted" data-id="element-9523" data-mp="cbthmxktaqei">
            <span data-id="element-9524" data-mp="1isorhxmoy0fo">Up to {naira(intent.budgetMax)} / year</span>
            <span data-id="element-9525" data-mp="1noccs1y1y9fw">Move-in {intent.moveIn}</span>
            <span data-id="element-9526" data-mp="387pxqxiermca">{intent.kinds.length === 0 ? 'All types' : intent.kinds.join(', ')}</span>
          </p>
        </div>
        <div className="flex items-center gap-2" data-id="element-9527" data-mp="33838fppb2fn4">
          <Link to="/housing/roommates" data-id="element-9528" data-mp="339enilo7b9tj">
            <Button variant="secondary" size="sm" icon={<UsersIcon className="h-3.5 w-3.5" data-id="element-9530" data-mp="2yglgts969ret" />} data-id="element-9529" data-mp="2yhmxcc8yj63x">
              Roommates
            </Button>
          </Link>
          <Button size="sm" variant="secondary" icon={<PencilIcon className="h-3.5 w-3.5" data-id="element-9532" data-mp="2oki7o2fwksid" />} onClick={() => setStage('intent')} data-id="element-9531" data-mp="2tijp6ixokjrq">
            Edit search
          </Button>
        </div>
      </div>

      <div className="flex gap-2 lg:hidden" data-id="element-9533" data-mp="2fi8x5ckmwz4z">
        <Button size="sm" variant={mobileView === 'list' ? 'primary' : 'secondary'} icon={<ListIcon className="h-3.5 w-3.5" data-id="element-9535" data-mp="2amaem5lzmbch" />} onClick={() => setMobileView('list')} data-id="element-9534" data-mp="2fjahy8rgczzw">
          List
        </Button>
        <Button size="sm" variant={mobileView === 'map' ? 'primary' : 'secondary'} icon={<MapIcon className="h-3.5 w-3.5" data-id="element-9537" data-mp="238jd26zozsx9" />} onClick={() => setMobileView('map')} data-id="element-9536" data-mp="8s2tk181h4st">
          Map
        </Button>
        <Button size="sm" variant="secondary" className="ml-auto" icon={<SlidersHorizontalIcon className="h-3.5 w-3.5" data-id="element-9539" data-mp="1tcg8kuhdbi75" />} onClick={() => setFiltersOpen(true)} data-id="element-9538" data-mp="280u8oxnegsk0">
          Filters
        </Button>
      </div>

      {/* Filtering must be reachable on a phone — the rail is desktop-only. */}
      <Modal open={filtersOpen} onClose={() => setFiltersOpen(false)} title="Filter these listings" description={`${results.length} of ${listings.length} places match right now.`} data-id="element-9540" data-mp="2qyx4bvagdnqs">
        <FilterRail filters={filters} onChange={setFilters} resultCount={results.length} data-id="element-9541" data-mp="2gz69fshzd6q6" />
        <div className="mt-6 flex flex-wrap items-center gap-3" data-id="element-9542" data-mp="10lof44rot7ni">
          <Button variant="primary" onClick={() => setFiltersOpen(false)} data-id="element-9543" data-mp="15l11ypik6eq3">
            Show {results.length} place{results.length === 1 ? '' : 's'}
          </Button>
          <Button variant="ghost" onClick={() => setFilters(DEFAULT_FILTERS)} data-id="element-9544" data-mp="3jqyylucm0z5c">
            Clear filters
          </Button>
        </div>
      </Modal>

      <div className="grid gap-6 lg:grid-cols-[220px_minmax(0,1fr)] xl:grid-cols-[220px_minmax(0,1fr)_380px]" data-id="element-9545" data-mp="3su05vyd7owal">
        <aside className="hidden lg:block" data-id="element-9546" data-mp="3orn1bda7b4fe">
          <div className="sticky top-32 rounded-card border border-line bg-surface p-4" data-id="element-9547" data-mp="3opse1sbgj085">
            <FilterRail filters={filters} onChange={setFilters} resultCount={results.length} data-id="element-9548" data-mp="312k9jclurv76" />
          </div>
        </aside>

        <div className={mobileView === 'map' ? 'hidden lg:block' : ''} data-id="element-9549" data-mp="2w1eh7fw3u9y6">
          {stage === 'searching' ? <ul className="space-y-3" aria-busy="true" aria-label="Loading listings" data-id="element-9550" data-mp="3545u7k4mpdrx">
              {[0, 1, 2, 3].map(index => <li key={index} className="flex animate-pulse gap-4 rounded-card border border-line bg-surface p-4" data-id="element-9551" data-mp="2hd9ve31r9ib6">
                  <div className="hidden h-28 w-40 shrink-0 rounded-lg bg-line sm:block" data-id="element-9552" data-mp="327sdgbhwkaou" />
                  <div className="flex-1 space-y-2.5 py-1" data-id="element-9553" data-mp="376bym9oo9ig5">
                    <div className="h-3.5 w-2/3 rounded bg-line" data-id="element-9554" data-mp="376vihvxvugwt" />
                    <div className="h-3 w-1/3 rounded bg-line" data-id="element-9555" data-mp="3b75mj3246x91" />
                    <div className="h-3 w-1/2 rounded bg-line" data-id="element-9556" data-mp="3g7a4yyml221w" />
                  </div>
                </li>)}
            </ul> : results.length === 0 ? <div className="rounded-card border border-line bg-surface" data-id="element-9557" data-mp="3l4aan8fit2sh">
              <EmptyState icon={<SearchXIcon className="h-4 w-4" data-id="element-9559" data-mp="3q34sa71r4lb0" />} title="No listings match those filters" description="Try widening the travel time, raising the budget, or turning off the verified-only filter to see everything available near this campus." action={<Button variant="secondary" onClick={() => setFilters(DEFAULT_FILTERS)} data-id="element-9560" data-mp="7pni75ljw8dd">
                    Clear filters
                  </Button>} data-id="element-9558" data-mp="3q3wnhgrfsh0r" />
            </div> : <motion.ul className="space-y-3" initial={{
          opacity: 0,
          y: 6
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.2,
          ease: [0.23, 1, 0.32, 1]
        }} data-id="element-9561" data-mp="hvomr7ynye74">
              {results.map(listing => <li key={listing.id} data-id="element-9562" data-mp="8x3b7wrlzzfu">
                  <ListingCard listing={listing} selected={listing.id === activeId} onHover={setActiveId} onSelect={setActiveId} data-id="element-9563" data-mp="15l9as59oc1n9" />
                </li>)}
            </motion.ul>}
        </div>

        <div className={mobileView === 'list' ? 'hidden xl:block' : 'block xl:block'} data-id="element-9564" data-mp="rsjjblbdm4ir">
          <div className="sticky top-32 h-[420px] overflow-hidden rounded-card border border-line xl:h-[calc(100dvh-11rem)]" data-id="element-9565" data-mp="1obr1y5euyp6i">
            {place && <ListingMap listings={results} campus={{
            lat: place.campus.lat,
            lng: place.campus.lng,
            name: place.campus.name
          }} activeId={activeId} onSelect={setActiveId} data-id="element-9566" data-mp="1jixuvavx34r9" />}
          </div>
        </div>
      </div>
    </div>;
}